import { forward as mgrsForward, toPoint as mgrsToPoint } from 'mgrs';

const a = 6378137;
const f = 1 / 298.257223563;
const k0 = 0.9996;
const e2 = 2 * f - f * f;
const ep2 = e2 / (1 - e2);

export function toMGRS(lat: number, lng: number, precision: number = 5): string {
  try {
    return mgrsForward([lng, lat], precision);
  } catch {
    return '---';
  }
}

export function parseMGRS(mgrs: string): { lng: number; lat: number; precision: number } | null {
  try {
    const cleaned = mgrs.trim().toUpperCase().replace(/\s+/g, '');
    const [lng, lat] = mgrsToPoint(cleaned);

    let precision = 0;
    const match = cleaned.match(/[A-Z]{3}(\d+)$/);
    if (match) {
      precision = match[1].length / 2;
    }

    return { lng, lat, precision };
  } catch {
    return null;
  }
}

export function mgrsZoomLevel(precision: number): number {
  if (precision >= 5) return 18;
  if (precision >= 4) return 16;
  if (precision >= 3) return 15;
  if (precision >= 2) return 13;
  if (precision >= 1) return 10;
  return 7;
}

function getUtmZone(lng: number): number {
  return Math.floor((lng + 180) / 6) + 1;
}

function getCentralMeridian(zone: number): number {
  return (zone - 1) * 6 - 180 + 3;
}

function latLngToUtm(lat: number, lng: number) {
  const zone = getUtmZone(lng);
  const cm = getCentralMeridian(zone);

  const latRad = lat * Math.PI / 180;
  const dlng = (lng - cm) * Math.PI / 180;

  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const tanLat = Math.tan(latRad);

  const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
  const T = tanLat * tanLat;
  const C = ep2 * cosLat * cosLat;
  const A = cosLat * dlng;

  const M = a * (
    (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256) * latRad
    - (3 * e2 / 8 + 3 * e2 * e2 / 32 + 45 * e2 * e2 * e2 / 1024) * Math.sin(2 * latRad)
    + (15 * e2 * e2 / 256 + 45 * e2 * e2 * e2 / 1024) * Math.sin(4 * latRad)
    - (35 * e2 * e2 * e2 / 3072) * Math.sin(6 * latRad)
  );

  const easting = k0 * N * (
    A + (1 - T + C) * A * A * A / 6
    + (5 - 18 * T + T * T + 72 * C - 58 * ep2) * A * A * A * A * A / 120
  ) + 500000;

  let northing = k0 * (
    M + N * tanLat * (
      A * A / 2
      + (5 - T + 9 * C + 4 * C * C) * A * A * A * A / 24
      + (61 - 58 * T + T * T + 600 * C - 330 * ep2) * A * A * A * A * A * A / 720
    )
  );

  if (lat < 0) northing += 10000000;

  return { easting, northing, zone, northern: lat >= 0 };
}

function utmToLatLng(easting: number, northing: number, zone: number, northern: boolean) {
  const cm = getCentralMeridian(zone);

  let N1 = northing;
  if (!northern) N1 -= 10000000;

  const M = N1 / k0;
  const mu = M / (a * (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256));

  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));

  const phi1 = mu
    + (3 * e1 / 2 - 27 * e1 * e1 * e1 / 32) * Math.sin(2 * mu)
    + (21 * e1 * e1 / 16 - 55 * e1 * e1 * e1 * e1 / 32) * Math.sin(4 * mu)
    + (151 * e1 * e1 * e1 / 96) * Math.sin(6 * mu)
    + (1097 * e1 * e1 * e1 * e1 / 512) * Math.sin(8 * mu);

  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = Math.tan(phi1);

  const N1r = a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1);
  const T1 = tanPhi1 * tanPhi1;
  const C1 = ep2 * cosPhi1 * cosPhi1;
  const R1 = a * (1 - e2) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5);
  const D = (easting - 500000) / (N1r * k0);

  const lat = phi1 - (N1r * tanPhi1 / R1) * (
    D * D / 2
    - (5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D * D * D * D / 24
    + (61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) * D * D * D * D * D * D / 720
  );

  const lng = (
    D - (1 + 2 * T1 + C1) * D * D * D / 6
    + (5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * D * D * D * D * D / 120
  ) / cosPhi1;

  return {
    lat: lat * 180 / Math.PI,
    lng: lng * 180 / Math.PI + cm
  };
}

interface GridBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

interface GridLevel {
  spacing: number;
  labelDigits: number;
}

function getGridLevel(zoom: number): GridLevel | null {
  if (zoom >= 15) return { spacing: 100, labelDigits: 3 };
  if (zoom >= 12) return { spacing: 1000, labelDigits: 2 };
  if (zoom >= 9) return { spacing: 10000, labelDigits: 1 };
  if (zoom >= 6) return { spacing: 100000, labelDigits: 0 };
  return null;
}

function formatEastingNorthing(value: number, spacing: number, digits: number): string {
  if (digits === 0) return '';
  const scaled = Math.floor(value / (spacing)) * spacing;
  if (spacing >= 100000) {
    return String(Math.floor(scaled / 100000) % 10);
  }
  const withinSquare = Math.floor(scaled % 100000);
  const divisor = Math.pow(10, 5 - digits);
  const val = Math.floor(withinSquare / divisor);
  return String(val).padStart(digits, '0');
}

export function generateMGRSGrid(
  bounds: GridBounds,
  zoom: number
): GeoJSON.FeatureCollection {
  const level = getGridLevel(zoom);
  if (!level) return { type: 'FeatureCollection', features: [] };

  const { spacing, labelDigits } = level;

  const centerLat = (bounds.south + bounds.north) / 2;
  const centerLng = (bounds.west + bounds.east) / 2;
  const zone = getUtmZone(centerLng);
  const northern = centerLat >= 0;

  const zoneCm = getCentralMeridian(zone);
  const clampedWest = Math.max(bounds.west, zoneCm - 3.5);
  const clampedEast = Math.min(bounds.east, zoneCm + 3.5);

  let sw: ReturnType<typeof latLngToUtm>;
  let ne: ReturnType<typeof latLngToUtm>;
  try {
    sw = latLngToUtm(bounds.south, clampedWest);
    ne = latLngToUtm(bounds.north, clampedEast);
  } catch {
    return { type: 'FeatureCollection', features: [] };
  }

  if (sw.zone !== zone) sw = latLngToUtm(bounds.south, centerLng);
  if (ne.zone !== zone) ne = latLngToUtm(bounds.north, centerLng);

  const minE = Math.floor(sw.easting / spacing) * spacing - spacing;
  const maxE = Math.ceil(ne.easting / spacing) * spacing + spacing;
  const rawMinN = Math.min(sw.northing, ne.northing);
  const rawMaxN = Math.max(sw.northing, ne.northing);
  const minN = Math.floor(rawMinN / spacing) * spacing - spacing;
  const maxN = Math.ceil(rawMaxN / spacing) * spacing + spacing;

  const eastingCount = Math.round((maxE - minE) / spacing);
  const northingCount = Math.round((maxN - minN) / spacing);
  if (eastingCount > 50 || northingCount > 50 || eastingCount < 0 || northingCount < 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  const features: GeoJSON.Feature[] = [];
  const steps = 40;

  for (let e = minE; e <= maxE; e += spacing) {
    const coords: number[][] = [];
    for (let i = 0; i <= steps; i++) {
      const n = minN + ((maxN - minN) * i / steps);
      try {
        const ll = utmToLatLng(e, n, zone, northern);
        if (ll.lat >= -80 && ll.lat <= 84) {
          coords.push([ll.lng, ll.lat]);
        }
      } catch { /* skip */ }
    }
    if (coords.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { lineType: 'easting' }
      });
    }
  }

  for (let n = minN; n <= maxN; n += spacing) {
    const coords: number[][] = [];
    for (let i = 0; i <= steps; i++) {
      const e = minE + ((maxE - minE) * i / steps);
      try {
        const ll = utmToLatLng(e, n, zone, northern);
        if (ll.lat >= -80 && ll.lat <= 84) {
          coords.push([ll.lng, ll.lat]);
        }
      } catch { /* skip */ }
    }
    if (coords.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { lineType: 'northing' }
      });
    }
  }

  for (let e = minE; e <= maxE; e += spacing) {
    for (let n = minN; n <= maxN; n += spacing) {
      try {
        const ll = utmToLatLng(e, n, zone, northern);
        if (ll.lat >= bounds.south - 0.01 && ll.lat <= bounds.north + 0.01 &&
            ll.lng >= bounds.west - 0.01 && ll.lng <= bounds.east + 0.01) {
          let label: string;
          if (labelDigits === 0) {
            label = toMGRS(ll.lat, ll.lng, 0);
          } else {
            const eLabel = formatEastingNorthing(e, spacing, labelDigits);
            const nLabel = formatEastingNorthing(n, spacing, labelDigits);
            label = eLabel + ' ' + nLabel;
          }
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [ll.lng, ll.lat] },
            properties: { label, lineType: 'label' }
          });
        }
      } catch { /* skip */ }
    }
  }

  return { type: 'FeatureCollection', features };
}
